/**
 * Pure projection + geometry helpers for the lag-detail line chart.
 *
 * No React, no DOM — all the chart's non-trivial logic lives here so it can be
 * unit-tested directly (this repo runs vitest in a node environment with no
 * jsdom; see `IndexerPipelineCard.test.tsx`). `LagLineChart.tsx` is a thin SVG
 * view over `buildChartModel`'s output.
 *
 * The chart reuses the SAME data the bars consume (`lagBuckets[]`) and the
 * SAME severity classifier (`lagToTone`), so a green bar and a green line mean
 * the same thing for the same reason. The bar encodes magnitude relative to
 * the row's own peak; the chart adds an ABSOLUTE seconds axis so the operator
 * can read the actual value off the gridlines.
 */

import { lagToTone, type LagTone, type Pipeline } from './lag-bars';
import { formatAxisTime, type LagWindow } from './lag-time';

export type LagMetric = 'p95' | 'max';

/** Structural subset of `LagBucketView` the chart needs. */
export interface LagSeriesBucket {
  startAt: string;
  endAt: string;
  lagP95Sec: number | null;
  lagMaxSec: number | null;
  sampleCount: number;
  okCount?: number;
  warnCount?: number;
  downCount?: number;
  totalProbes?: number;
}

export interface SeriesPoint {
  idx: number;
  /** Bucket start as epoch ms, or `null` when the bucket has no timestamp. */
  t: number | null;
  /** Metric value in seconds, or `null` for a gap (no data / lag not measured). */
  y: number | null;
  /** Severity for this bucket: failure counts win, else threshold of `y`. */
  tone: LagTone;
}

export interface LagSeries {
  points: SeriesPoint[];
  /** Largest non-null `y` across the series (0 when the series is empty). */
  maxSec: number;
}

const SEVERITY_RANK: Record<LagTone, number> = {
  none: 0,
  ok: 1,
  warn: 2,
  down: 3,
};

/** The more severe of two tones (down > warn > ok > none). */
export function moreSevere(a: LagTone, b: LagTone): LagTone {
  return SEVERITY_RANK[a] >= SEVERITY_RANK[b] ? a : b;
}

/**
 * Project lag buckets into a time series for one metric. Mirrors
 * `LagHistoryBar`'s per-bucket tone logic exactly: a bucket with failures
 * (`downCount`/`warnCount`) takes that severity even when no lag was measured;
 * otherwise the tone comes from `lagToTone(value, pipeline)`. `null` metric
 * values are preserved as gaps so the line breaks instead of interpolating
 * across missing data.
 */
export function bucketsToSeries(
  buckets: readonly LagSeriesBucket[],
  metric: LagMetric,
  pipeline: Pipeline,
): LagSeries {
  let maxSec = 0;
  const points = buckets.map((b, idx): SeriesPoint => {
    const value = metric === 'p95' ? b.lagP95Sec : b.lagMaxSec;
    const down = b.downCount ?? 0;
    const warn = b.warnCount ?? 0;
    const failureTone: LagTone | null =
      down > 0 ? 'down' : warn > 0 ? 'warn' : null;
    const tone = failureTone ?? lagToTone(value, pipeline);
    const parsed = b.startAt ? Date.parse(b.startAt) : Number.NaN;
    const t = Number.isFinite(parsed) ? parsed : null;
    if (value !== null && value > maxSec) maxSec = value;
    return { idx, t, y: value, tone };
  });
  return { points, maxSec };
}

/**
 * "Nice" axis ticks from 0 up to a rounded ceiling ≥ `maxSec`, using the
 * classic 1/2/5 × 10^n step so labels land on readable values. Handles
 * sub-second ranges (e.g. `maxSec = 0.4` → `[0, 0.1, 0.2, 0.3, 0.4]`) and a
 * degenerate all-zero/healthy window (falls back to a 1s ceiling so the axis
 * isn't a single flat line).
 */
export function niceLagTicks(maxSec: number, targetCount = 4): number[] {
  const safeMax = Number.isFinite(maxSec) && maxSec > 0 ? maxSec : 1;
  const tc =
    Number.isFinite(targetCount) && targetCount >= 1
      ? Math.floor(targetCount)
      : 4;
  const rawStep = safeMax / tc;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const residual = rawStep / magnitude;
  const niceResidual =
    residual <= 1 ? 1 : residual <= 2 ? 2 : residual <= 5 ? 5 : 10;
  const step = niceResidual * magnitude;
  const ceiling = Math.ceil(safeMax / step) * step;
  const ticks: number[] = [];
  // Use integer indices to avoid float drift on the running sum.
  const count = Math.round(ceiling / step);
  for (let i = 0; i <= count; i += 1) {
    ticks.push(roundSec(i * step));
  }
  return ticks;
}

/** Round to 3 decimals to kill binary-float noise (e.g. 0.30000000004). */
function roundSec(v: number): number {
  return Math.round(v * 1000) / 1000;
}

export interface ChartDims {
  width: number;
  height: number;
  padLeft: number;
  padRight: number;
  padTop: number;
  padBottom: number;
}

export const DEFAULT_CHART_DIMS: ChartDims = {
  width: 640,
  height: 200,
  padLeft: 34,
  padRight: 10,
  padTop: 12,
  padBottom: 22,
};

export interface AxisTick {
  v: number;
  y: number;
}

export interface LineSegment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  tone: LagTone;
}

export interface AreaSegment {
  /** SVG path for the trapezoid under one line segment. */
  d: string;
  tone: LagTone;
}

export interface PointMarker {
  x: number;
  y: number;
  tone: LagTone;
}

export interface XLabel {
  x: number;
  label: string;
}

export interface ChartModel {
  dims: ChartDims;
  ceilingSec: number;
  baselineY: number;
  ticks: AxisTick[];
  /** Per-point SVG coords; `null` entries are gaps (no line vertex). */
  coords: ({ x: number; y: number } | null)[];
  lineSegments: LineSegment[];
  /** One trapezoid per line segment, coloured per range. */
  areaSegments: AreaSegment[];
  /** Markers on warn/down buckets that DO have a measured value. */
  markers: PointMarker[];
  xLabels: XLabel[];
}

/**
 * Build the full SVG model for a lag series: y-axis ticks against an absolute
 * seconds ceiling, per-point coords, per-segment colored line, a soft single
 * area fill (broken at gaps), markers for warn/down spikes, and ~3 x-axis time
 * labels. Pure — deterministic for given inputs.
 */
export function buildChartModel(
  series: LagSeries,
  windowSel: LagWindow,
  dims: ChartDims = DEFAULT_CHART_DIMS,
): ChartModel {
  const { points } = series;
  const ticks = niceLagTicks(series.maxSec);
  const ceilingSec = ticks.length > 0 ? ticks[ticks.length - 1] : 1;
  const innerW = dims.width - dims.padLeft - dims.padRight;
  const innerH = dims.height - dims.padTop - dims.padBottom;
  const baselineY = dims.padTop + innerH;
  const n = points.length;

  const xOf = (idx: number): number =>
    n <= 1 ? dims.padLeft + innerW / 2 : dims.padLeft + (idx / (n - 1)) * innerW;
  const yOf = (v: number): number => {
    const clamped = v < 0 ? 0 : v > ceilingSec ? ceilingSec : v;
    return dims.padTop + innerH * (1 - clamped / ceilingSec);
  };

  const coords = points.map((p) =>
    p.y === null ? null : { x: xOf(p.idx), y: yOf(p.y) },
  );

  // Vertices = buckets with a measured value, in order. The line/area BRIDGE
  // across null buckets (a missed sample or a total outage with no measurable
  // lag) instead of plunging to the baseline — a drop-to-zero would read as
  // "lag = 0" (good) when it was actually no-data/an outage (the opposite).
  const verts: { x: number; y: number; idx: number }[] = [];
  for (let i = 0; i < n; i += 1) {
    const c = coords[i];
    if (c) verts.push({ x: c.x, y: c.y, idx: i });
  }

  // Worst tone across an inclusive bucket-index range, so a bridged segment
  // that spans a null OUTAGE is still coloured red (the outage isn't hidden).
  const worstBetween = (lo: number, hi: number): LagTone => {
    let t: LagTone = 'none';
    for (let i = lo; i <= hi; i += 1) t = moreSevere(t, points[i].tone);
    return t;
  };

  // Each segment carries its own colour for BOTH the line and the area fill
  // beneath it, so a single red span tints only its own range — not the whole
  // chart. Trapezoids tile edge-to-edge (no overlap → no double-darkening).
  const lineSegments: LineSegment[] = [];
  const areaSegments: AreaSegment[] = [];
  for (let k = 1; k < verts.length; k += 1) {
    const a = verts[k - 1];
    const b = verts[k];
    const tone = worstBetween(a.idx, b.idx);
    lineSegments.push({ x1: a.x, y1: a.y, x2: b.x, y2: b.y, tone });
    areaSegments.push({
      d: `M ${fmt(a.x)} ${fmt(baselineY)} L ${fmt(a.x)} ${fmt(a.y)} L ${fmt(b.x)} ${fmt(b.y)} L ${fmt(b.x)} ${fmt(baselineY)} Z`,
      tone,
    });
  }

  const markers: PointMarker[] = [];
  for (let i = 0; i < n; i += 1) {
    const p = points[i];
    const c = coords[i];
    if (c && (p.tone === 'warn' || p.tone === 'down')) {
      markers.push({ x: c.x, y: c.y, tone: p.tone });
    }
  }

  const xLabels = buildXLabels(points, xOf, windowSel);

  return {
    dims,
    ceilingSec,
    baselineY,
    ticks: ticks.map((v) => ({ v, y: yOf(v) })),
    coords,
    lineSegments,
    areaSegments,
    markers,
    xLabels,
  };
}

/** Up to 3 evenly-spaced x-axis labels (first / middle / last with a `t`). */
function buildXLabels(
  points: readonly SeriesPoint[],
  xOf: (idx: number) => number,
  windowSel: LagWindow,
): XLabel[] {
  const n = points.length;
  if (n === 0) return [];
  const idxs = n === 1 ? [0] : [0, Math.floor((n - 1) / 2), n - 1];
  const seen = new Set<number>();
  const labels: XLabel[] = [];
  for (const idx of idxs) {
    if (seen.has(idx)) continue;
    seen.add(idx);
    const t = points[idx].t;
    if (t === null) continue;
    const label = formatAxisTime(t, windowSel);
    if (label) labels.push({ x: xOf(idx), label });
  }
  return labels;
}

/** Trim coordinate floats to 2 decimals for compact, stable path strings. */
function fmt(v: number): string {
  return String(Math.round(v * 100) / 100);
}
